import { Add, Delete, Edit, MoreVert, PlaylistAdd, Sync, Tv, Visibility } from '@mui/icons-material'
import { Box, Button, Dialog, DialogContent, DialogTitle, Fab, IconButton, InputAdornment, ListItemIcon, ListItemText, Menu, MenuItem, Modal, TextField, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material'
import { AppPageHeader, StatusChip, TealButton } from '../../Components/ui'
import { GridRowParams } from '@mui/x-data-grid/models/params/gridRowParams'
import { memo, useCallback, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { getColumns } from '../../Components/ColumnsDefinitions/PlaylistColumns'
import { CustomQuickFilter, CustomToolbarContainer, StyledDataGrid } from '../../Components/DataGrid'
import EmptyState from '../../Components/EmptyState'
import DialogQuiz from '../../Components/Modal/DialogQuiz'
import ModalHeader from '../../Components/Modal/ModalHeader'
import { Playlist } from '../../Model/PlaylistModel'
import { PriceTable } from '../../Model/PriceTableModel'
import { useFindAllMediaQuery } from '../../service/MediaService'
import { useDeletePlaylistMutation, useFindAllPlaylistsQuery } from '../../service/PlaylistService'
import { useFindAllPriceTablesQuery } from '../../service/PriceTableService'
import { useFindAllDefaultTemplatesQuery, useFindAllTemplatesQuery } from '../../service/TemplateService'
import { useAppSelector } from '../../store/hooks'
import { createPriceTableDisplays } from '../../Util/parseHTML'
import { useFindAllProductsQuery } from '../../service/ProductService'
import { useUpdatePriceTableLayoutsMutation } from '../../service/PriceTableLayoutService'
import { useBroadcastUserUpdateMutation } from '../../service/DeviceSessionService'
import Helper from './Helper'
import SearchIcon from '@mui/icons-material/Search'

// ─── Modal Painel TV (video wall) ─────────────────────────────────────────────
const SITE = 'https://thbamidia.com.br'

const WallModal = ({ playlist, onClose }: { playlist: Playlist | null; onClose: () => void }) => {
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'
  const code = playlist?.code ?? ''
  const urlLeft  = `${SITE}/tv/${code}?side=left`
  const urlRight = `${SITE}/tv/${code}?side=right`

  const copy = (url: string) => {
    navigator.clipboard.writeText(url).catch(() => {})
    // feedback visual simples via title temporário — toast não necessário
  }

  return (
    <Dialog open={!!playlist} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 3, bgcolor: isDark ? '#111c22' : '#fff' } }}>
      <DialogTitle sx={{ fontWeight: 700, fontSize: 18, pb: 0.5 }}>
        📺 Painel TV — {playlist?.name}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
          Abra cada URL em uma TV. Juntas, elas formam um painel maior com o mesmo vídeo.
        </Typography>

        {/* Diagrama visual */}
        <Box sx={{ display: 'flex', gap: 1, mb: 3, justifyContent: 'center' }}>
          {['Esquerda', 'Direita'].map(side => (
            <Box key={side} sx={{
              flex: 1, height: 72, borderRadius: 2,
              border: `2px solid ${isDark ? '#1e3a2f' : '#a7f3d0'}`,
              bgcolor: isDark ? '#0f2218' : '#f0fdf9',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexDirection: 'column', gap: 0.5,
            }}>
              <Tv sx={{ fontSize: 20, color: '#14b8a0' }} />
              <Typography sx={{ fontSize: 11, color: 'text.secondary', fontWeight: 600 }}>
                TV {side}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* URLs */}
        {[{ label: 'TV Esquerda', url: urlLeft }, { label: 'TV Direita', url: urlRight }].map(({ label, url }) => (
          <Box key={label} sx={{ mb: 2 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>
              {label}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <TextField
                size="small" fullWidth value={url} InputProps={{ readOnly: true }}
                sx={{ '& input': { fontSize: 12, fontFamily: 'monospace' } }}
              />
              <Tooltip title="Copiar">
                <IconButton size="small" onClick={() => copy(url)}
                  sx={{ color: '#14b8a0', flexShrink: 0 }}>
                  <Add sx={{ fontSize: 16, transform: 'rotate(45deg)' }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        ))}

        <Typography variant="caption" color="text.disabled">
          Sync automático via relógio do sistema. Ambas as TVs devem ter conexão à internet.
        </Typography>
      </DialogContent>
    </Dialog>
  )
}

// ─── Card mobile de playlist ──────────────────────────────────────────────────
type PlaylistCardProps = {
  playlist: Playlist
  onEdit: () => void
  onDelete: () => void
  onView: () => void
  onWall: () => void
}

const PlaylistCard = ({ playlist, onEdit, onDelete, onView, onWall }: PlaylistCardProps) => {
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'
  const [anchor, setAnchor] = useState<null | HTMLElement>(null)

  const bg    = isDark ? '#152028' : '#ffffff'
  const bdr   = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)'
  const teal  = isDark ? '#14b8a0' : '#0d9488'
  const muted = isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)'

  return (
    <Box sx={{
      bgcolor: bg,
      border: `1px solid ${bdr}`,
      borderRadius: '14px',
      p: '14px 16px',
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      boxShadow: isDark ? 'none' : '0 1px 4px rgba(0,0,0,0.06)',
      transition: 'box-shadow 0.15s',
      '&:active': { opacity: 0.85 },
    }}>

      {/* Ícone */}
      <Box sx={{
        width: 42, height: 42, flexShrink: 0, borderRadius: '10px',
        background: isDark
          ? 'linear-gradient(135deg, #1a3540, #142830)'
          : 'linear-gradient(135deg, #e8f5f3, #d0ede9)',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : 'rgba(13,148,136,0.15)'}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: teal,
      }}>
        <PlaylistAdd sx={{ fontSize: 18 }} />
      </Box>

      {/* Texto */}
      <Box sx={{ flex: 1, minWidth: 0 }} onClick={onEdit}>
        <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'text.primary', mb: 0.3 }} noWrap>
          {playlist.name}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
            {playlist.items?.length ?? 0} {playlist.items?.length === 1 ? 'item' : 'itens'}
          </Typography>
          {playlist.code && (
            <>
              <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: muted }} />
              <Typography sx={{ fontSize: 11, color: 'text.disabled', fontFamily: 'monospace', letterSpacing: 0.5 }}>
                #{playlist.code}
              </Typography>
            </>
          )}
        </Box>
      </Box>

      {/* Ação rápida: editar */}
      <IconButton
        size="small"
        onClick={onEdit}
        sx={{
          width: 36, height: 36, color: 'text.disabled', flexShrink: 0,
          '&:hover': { color: teal, bgcolor: isDark ? 'rgba(20,184,160,0.1)' : 'rgba(13,148,136,0.08)' },
        }}
      >
        <Edit sx={{ fontSize: 16 }} />
      </IconButton>

      {/* Menu 3 pontos */}
      <IconButton
        size="small"
        onClick={e => setAnchor(e.currentTarget)}
        sx={{
          width: 36, height: 36, color: 'text.disabled', flexShrink: 0,
          '&:hover': { color: 'text.primary', bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)' },
        }}
      >
        <MoreVert sx={{ fontSize: 18 }} />
      </IconButton>

      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          sx: {
            borderRadius: '12px', minWidth: 160,
            boxShadow: isDark
              ? '0 8px 32px rgba(0,0,0,0.5)'
              : '0 8px 24px rgba(0,0,0,0.12)',
            border: `1px solid ${bdr}`,
          },
        }}
      >
        <MenuItem onClick={() => { setAnchor(null); onEdit() }} sx={{ py: 1.25, px: 2 }}>
          <ListItemIcon><Edit sx={{ fontSize: 16, color: 'text.secondary' }} /></ListItemIcon>
          <ListItemText primary={<Typography sx={{ fontSize: 14 }}>Editar</Typography>} />
        </MenuItem>
        <MenuItem onClick={() => { setAnchor(null); onView() }} sx={{ py: 1.25, px: 2 }}>
          <ListItemIcon><Visibility sx={{ fontSize: 16, color: 'text.secondary' }} /></ListItemIcon>
          <ListItemText primary={<Typography sx={{ fontSize: 14 }}>Visualizar</Typography>} />
        </MenuItem>
        <MenuItem onClick={() => { setAnchor(null); onWall() }} sx={{ py: 1.25, px: 2 }}>
          <ListItemIcon><Tv sx={{ fontSize: 16, color: 'text.secondary' }} /></ListItemIcon>
          <ListItemText primary={<Typography sx={{ fontSize: 14 }}>Painel TV</Typography>} />
        </MenuItem>
        <MenuItem onClick={() => { setAnchor(null); onDelete() }}
          sx={{ py: 1.25, px: 2, color: 'error.main' }}>
          <ListItemIcon><Delete sx={{ fontSize: 16, color: 'error.main' }} /></ListItemIcon>
          <ListItemText primary={<Typography sx={{ fontSize: 14, color: 'error.main' }}>Excluir</Typography>} />
        </MenuItem>
      </Menu>
    </Box>
  )
}

// ─── PlaylistsPage ────────────────────────────────────────────────────────────
const PlaylistsPage = () => {
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'
  const navigate = useNavigate()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const user       = useAppSelector(state => state.auth.user)
  const playlists  = useAppSelector(state => state.playlist)
  const medias     = useAppSelector(state => state.media)
  const priceTables = useAppSelector(state => state.priceTable)
  const templates  = useAppSelector(state => state.template)
  const products   = useAppSelector(state => state.product)

  const [openDialog, setOpenDialog]       = useState(false)
  const [openHelp, setOpenHelp]           = useState(false)
  const [openNameModal, setOpenNameModal] = useState(false)
  const [newPlaylistName, setNewPlaylistName] = useState('')
  const [searchQuery, setSearchQuery]     = useState('')
  const [wallPlaylist, setWallPlaylist]   = useState<Playlist | null>(null)
  const selectedId = useRef<number>(undefined)

  const [deletePlaylist]         = useDeletePlaylistMutation()
  const [updatePriceTableLayouts] = useUpdatePriceTableLayoutsMutation()
  const [broadcast]              = useBroadcastUserUpdateMutation()

  const userId = user?.id?.toString() || ''
  useFindAllMediaQuery(userId, { skip: medias.length > 0 })
  const { isSuccess: ptLoaded }   = useFindAllPriceTablesQuery(userId, { skip: priceTables.length > 0 })
  const { isSuccess: tmplLoaded } = useFindAllTemplatesQuery(userId, { skip: templates.length > 0 })
  useFindAllDefaultTemplatesQuery(undefined, { skip: templates.some(t => t.isDefault) })
  // Playlists só carregam após priceTables E templates estarem prontos (evita race condition no buildPlaylist)
  const priceTablesReady = priceTables.length > 0 || ptLoaded
  const templatesReady   = templates.length > 0   || tmplLoaded
  useFindAllPlaylistsQuery(userId, { skip: !userId || !priceTablesReady || !templatesReady })
  useFindAllProductsQuery(userId, { skip: products.length > 0 })

  const filteredPlaylists = useMemo(() => {
    if (!searchQuery.trim()) return playlists
    const q = searchQuery.toLowerCase()
    return playlists.filter(p =>
      p.name?.toLowerCase().includes(q) || p.code?.toLowerCase().includes(q)
    )
  }, [playlists, searchQuery])

  const handleAdd = useCallback(() => {
    setNewPlaylistName('')
    setOpenNameModal(true)
  }, [])

  const handleConfirmName = useCallback(() => {
    const name = newPlaylistName.trim()
    if (name.length < 3) return
    setOpenNameModal(false)
    navigate('/playlists/edit/0', { state: { name } })
  }, [newPlaylistName, navigate])

  async function handleUpdateTV(lists: Playlist[]) {
    if (!lists.length) return
    for (const playlist of lists) {
      // Resolve data/template "lazy" (item pode ter vindo só com _priceTableId/_templateId)
      const updatedItems = playlist.items.map(item => {
        if (item.type !== 'priceTable') return item
        return {
          ...item,
          data: (item.data ?? priceTables.find(pt => pt.id === item._priceTableId)) as PriceTable,
          template: item.template ?? templates.find(t => t.id === item._templateId),
        }
      })

      const tableItems = updatedItems.filter(i => i.type === 'priceTable')
      if (tableItems.length === 0) continue

      // Se algum item ainda não tem tabela/template resolvidos, pula essa playlist:
      // updatePriceTableLayouts substitui TODOS os layouts da playlist de uma vez.
      const incomplete = tableItems.some(item => !(item.data as PriceTable)?.id || !item.template)
      if (incomplete) continue

      const layouts = createPriceTableDisplays(updatedItems, products).map(l => ({
        ...l, id: undefined, playlistId: playlist.id ?? 0
      }))
      try { await updatePriceTableLayouts(layouts).unwrap() }
      catch { toast.error(`Falha ao atualizar layouts da playlist "${playlist.name}"`) }
    }
    if (user?.id)
      await broadcast(user.id.toString()).unwrap()
        .then(() => toast.success('Playlists atualizadas'))
        .catch(() => toast.error('Falha na atualização'))
  }

  const handleEdit = useCallback((params: GridRowParams<Playlist>) => {
    navigate(`/playlists/edit/${params.id as number}`)
  }, [navigate])

  const handleViewPlaylist = useCallback((params: GridRowParams<Playlist>) => {
    navigate(`/view/${params.row.code}`)
  }, [navigate])

  const handleDelete = useCallback((params: GridRowParams<Playlist>) => {
    selectedId.current = params.id as number
    setOpenDialog(true)
  }, [])

  const handleDeleteById = useCallback((id: number) => {
    selectedId.current = id
    setOpenDialog(true)
  }, [])

  async function handleCloseDialog(option: boolean) {
    setOpenDialog(false)
    if (option && selectedId.current)
      await deletePlaylist(selectedId.current).unwrap()
        .then(() => toast.success('Playlist excluída'))
        .catch(() => toast.error('Falha ao excluir playlist'))
  }

  const columns = useMemo(
    () => getColumns(handleEdit, handleDelete, handleViewPlaylist,
      (params) => setWallPlaylist(params.row))[isMobile ? 1 : 0],
    [handleEdit, handleDelete, handleViewPlaylist, isMobile]
  )

  const SearchToolbar = memo(() => (
    <CustomToolbarContainer sx={{ height: 48, display: 'flex', alignItems: 'center', px: 1 }}>
      <CustomQuickFilter placeholder="Buscar playlist..." variant="filled" />
    </CustomToolbarContainer>
  ))

  const teal   = isDark ? '#14b8a0' : '#0d9488'
  const bdr    = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)'

  // ─── Modais (compartilhados desktop + mobile) ────────────────────────────────
  const modals = (
    <>
      {/* Modal: nome da nova playlist */}
      <Modal open={openNameModal} onClose={() => setOpenNameModal(false)}>
        <Box sx={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%,-50%)',
          bgcolor: 'background.paper', borderRadius: 3,
          p: isMobile ? 3 : 4,
          width: isMobile ? 'calc(100vw - 48px)' : 400,
          boxShadow: '0 24px 48px rgba(0,0,0,0.18)',
          outline: 'none',
        }}>
          <Typography variant="h6" fontWeight={700} mb={0.5}>Nova Playlist</Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>
            Dê um nome para identificar esta playlist nas TVs.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Nome da playlist"
            placeholder="Ex: Promoções da Semana"
            value={newPlaylistName}
            onChange={e => setNewPlaylistName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleConfirmName()}
            error={newPlaylistName.trim().length > 0 && newPlaylistName.trim().length < 3}
            helperText={newPlaylistName.trim().length > 0 && newPlaylistName.trim().length < 3 ? 'Mínimo 3 caracteres' : ' '}
            inputProps={{ style: { fontSize: 16 } }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 2 }}>
            <Button variant="outlined" onClick={() => setOpenNameModal(false)}
              sx={{ borderRadius: 2, px: 3 }}>
              Cancelar
            </Button>
            <Button variant="contained" onClick={handleConfirmName}
              disabled={newPlaylistName.trim().length < 3}
              sx={{
                borderRadius: 2, px: 3,
                background: `linear-gradient(135deg, ${teal} 0%, #0d9488 100%)`,
                '&:hover': { background: 'linear-gradient(135deg, #0d9488 0%, #0a7a70 100%)' },
              }}>
              Criar
            </Button>
          </Box>
        </Box>
      </Modal>

      <DialogQuiz title="Confirmar exclusão?" text="Esta playlist será excluída permanentemente."
        open={openDialog} handleClose={handleCloseDialog} variant="warning" />

      {/* Modal: Painel TV (video wall) */}
      <WallModal playlist={wallPlaylist} onClose={() => setWallPlaylist(null)} />

      <Modal open={openHelp} onClose={() => setOpenHelp(false)}>
        <Box sx={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%,-50%)',
          bgcolor: 'background.paper', borderRadius: 2, p: 4,
          maxWidth: 560, width: '90%', maxHeight: '85vh', overflowY: 'auto',
        }}>
          <ModalHeader title="Playlists" onClose={() => setOpenHelp(false)} />
          <Helper />
        </Box>
      </Modal>
    </>
  )

  // ─── Mobile ──────────────────────────────────────────────────────────────────
  if (isMobile) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }}>
        {modals}

        <AppPageHeader
          title="Playlists"
          count={playlists.length}
          helpContent={<Helper />}
          mobileActions={
            <Tooltip title="Atualizar TVs">
              <IconButton size="small" onClick={() => handleUpdateTV(playlists)}
                sx={{ width: 36, height: 36, color: 'text.disabled' }}>
                <Sync sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          }
        />

        {/* Busca */}
        <Box sx={{ px: 2, py: 1.25, flexShrink: 0, bgcolor: 'background.default' }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Buscar playlist..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            inputProps={{ style: { fontSize: 15 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 17, color: 'text.disabled' }} />
                </InputAdornment>
              ),
              sx: {
                borderRadius: '10px',
                bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: teal,
                  borderWidth: 1.5,
                },
              },
            }}
          />
        </Box>

        {/* Lista de cards */}
        <Box sx={{ flex: 1, overflowY: 'auto', px: 2, pb: '88px' }}>
          {filteredPlaylists.length === 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 8, gap: 1.5, color: 'text.disabled' }}>
              <PlaylistAdd sx={{ fontSize: 48, opacity: 0.3 }} />
              <Typography sx={{ fontSize: 15, fontWeight: 500, color: 'text.secondary' }}>
                {searchQuery ? 'Nenhum resultado encontrado' : 'Nenhuma playlist criada'}
              </Typography>
              {!searchQuery && (
                <Typography sx={{ fontSize: 13, color: 'text.disabled', textAlign: 'center', px: 4 }}>
                  Toque no botão + para criar sua primeira playlist
                </Typography>
              )}
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {filteredPlaylists.map(p => (
                <PlaylistCard
                  key={p.id ?? p.name}
                  playlist={p}
                  onEdit={() => navigate(`/playlists/edit/${p.id}`)}
                  onView={() => navigate(`/view/${p.code}`)}
                  onDelete={() => handleDeleteById(p.id!)}
                  onWall={() => setWallPlaylist(p)}
                />
              ))}
            </Box>
          )}
        </Box>

        {/* FAB */}
        <Fab
          onClick={handleAdd}
          sx={{
            position: 'fixed',
            bottom: 'calc(env(safe-area-inset-bottom) + 24px)',
            right: 24,
            background: `linear-gradient(135deg, ${teal} 0%, #0d9488 100%)`,
            color: '#fff',
            boxShadow: '0 6px 20px rgba(20,184,160,0.4)',
            '&:hover': {
              background: 'linear-gradient(135deg, #0d9488 0%, #0a7a70 100%)',
              boxShadow: '0 8px 28px rgba(20,184,160,0.5)',
            },
            '&:active': { transform: 'scale(0.94)' },
            transition: 'all 0.2s ease',
          }}
        >
          <Add />
        </Fab>
      </Box>
    )
  }

  // ─── Desktop ──────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {modals}

      <AppPageHeader
        title="Playlists"
        count={playlists.length}
        helpContent={<Helper />}
        actions={
          <>
            <Tooltip title="Atualizar TVs">
              <Button size="small" variant="outlined" onClick={() => handleUpdateTV(playlists)}
                startIcon={<Sync />} sx={{ flexShrink: 0, height: 36 }}>
                Atualizar TVs
              </Button>
            </Tooltip>
            <TealButton size="md" onClick={handleAdd} startIcon={<Add />}>
              Nova Playlist
            </TealButton>
          </>
        }
      />

      {/* DataGrid */}
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden', px: 2, py: 1.5 }}>
        <StyledDataGrid
          columnHeaderHeight={50}
          rowHeight={52}
          sx={{ width: '100%' }}
          rows={playlists}
          columns={columns}
          getRowId={row => row.id || row.name}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
          pageSizeOptions={[10, 25, 50]}
          slots={{
            toolbar: SearchToolbar,
            noRowsOverlay: () => (
              <EmptyState icon={PlaylistAdd} title="Nenhuma playlist criada"
                subtitle='Clique em "Nova Playlist" para começar' />
            ),
          }}
          disableRowSelectionOnClick
          onRowDoubleClick={params => navigate('edit/' + params.id)}
        />
      </Box>
    </Box>
  )
}

export default PlaylistsPage
